import type { FC } from "react";
import { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  TabButton,
  HelpLabel,
  StoryblokHelpBlock,
  OrderSuccessPopup,
  LGBDatepicker,
  PageLayout,
} from "@components";
import { Toast, Button, Spinner } from "flowbite-react";
import { RoutePlanner } from "./RoutePlanner";
import { OrderPlanner } from "./OrderPlanner";
import { OrderStatusFilterDropdown } from "./components/OrderStatusFilterDropdown";
import { useGlobalOrders } from "../../hooks";
import { useWorkspaceData, useDataCache } from "@data";
import { useWorkspace } from "../../context/WorkspaceContext";
import { IOrder, IRouteItem } from "../../types";
import { HiCheck } from "react-icons/hi";
import { updateDriverSchedule, assignOrder } from "../../api/client";
import { useUserSession } from "../../context/UserContext";
import moment from "moment";
import { OrderStatus } from "../../types/order/IOrder";
import { pageTabBar, Inline } from "@design-system";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";

type OrderPlannerState = {
  hasChanges: boolean;
  changesCount: number;
  pendingChanges: { orderId: string; driverId: string }[];
  localOrders: IOrder[];
};

export const PlannerPage: FC = function () {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Hent tab, dato og sjåfør fra URL-parametere
  const tabFromUrl = searchParams.get("tab") as "order" | "route" | null;
  const dateFromUrl = searchParams.get("date");
  const driverFromUrl = searchParams.get("driver");
  const showSuccessPopupParam = searchParams.get("showSuccessPopup");
  const orderIdFromUrl = searchParams.get("orderId");

  const [showSuccessPopup, setShowSuccessPopup] = useState(
    showSuccessPopupParam === "true",
  );
  const [activeTab, setActiveTab] = useState<"order" | "route">(
    tabFromUrl || "route",
  );
  const { t } = useTranslation(["common", "planner", "stats"]);
  const { activeWorkspace } = useWorkspace();
  const { userSession } = useUserSession();
  const { invalidateOrders } = useDataCache();
  const [isUpdating, setIsUpdating] = useState(false);
  const [shouldReset, setShouldReset] = useState(false);
  const [showRouteSaveSuccessToast, setShowRouteSaveSuccessToast] =
    useState(false);

  // Separate driver selection states for order and route tabs
  const [orderSelectedDriver, setOrderSelectedDriver] = useState<string>("");
  const [routeSelectedDriver, setRouteSelectedDriver] = useState<string>(
    driverFromUrl || "",
  );

  const [selectedDate, setSelectedDate] = useState<Date>(
    dateFromUrl ? new Date(dateFromUrl) : new Date(),
  );
  const [selectedStatuses, setSelectedStatuses] = useState<OrderStatus[]>([
    OrderStatus.New,
    OrderStatus.Scheduled,
  ]);
  const [plannerState, setPlannerState] = useState<OrderPlannerState>({
    hasChanges: false,
    changesCount: 0,
    pendingChanges: [],
    localOrders: [],
  });
  const [routePlannerState, setRoutePlannerState] = useState<{
    hasChanges: boolean;
    changesCount: number;
    routeStops: (IRouteItem & { isAvailable: boolean })[];
    availableStops: (IRouteItem & { isAvailable: boolean })[];
    hasValidationErrors: boolean;
  }>({
    hasChanges: false,
    changesCount: 0,
    routeStops: [],
    availableStops: [],
    hasValidationErrors: false,
  });

  // Memoize workspace ID to prevent unnecessary query changes
  const workspaceId = useMemo(
    () => activeWorkspace?.workspaceId ?? "",
    [activeWorkspace?.workspaceId],
  );

  const orderQuery = useGlobalOrders(); // Use global hook for cost optimization
  // Drivers come from the centrally-loaded data store.
  const { drivers } = useWorkspaceData();

  // Memoize data transformations
  const orders = useMemo(() => orderQuery.data ?? [], [orderQuery.data]);

  // Admin: Open Firestore routes query for selected date
  const firebaseProjectId =
    (process.env.REACT_APP_FIREBASE_PROJECT_ID as string) || "logibud-dev-app";
  const handleOpenFirestoreRoutesForDate = useCallback(() => {
    if (!selectedDate) return;
    const dateOnly = moment(selectedDate).format("YYYY-MM-DD");
    const prevDateOnly = moment(selectedDate)
      .subtract(1, "day")
      .format("YYYY-MM-DD");
    const routesDocSegment = "~2Froutes~2F7dSezmNDFlfsaQ16Hg8D";
    const queryString = `3|LIM|3/100|WH|1|9/routeDate|GTE|TS|24/${prevDateOnly}T22:00:00.559Z|WH|1|9/routeDate|LTE|TS|24/${dateOnly}T21:59:59.355Z`;
    const encodedQuery = encodeURIComponent(queryString);
    const scopeName = encodeURIComponent("/routes");
    const url = `https://console.firebase.google.com/u/1/project/${firebaseProjectId}/firestore/databases/-default-/data/${routesDocSegment}?view=query-view&query=${encodedQuery}&scopeType=collection&scopeName=${scopeName}`;
    window.open(url, "_blank", "noopener");
  }, [selectedDate, firebaseProjectId]);

  // Oppdater URL når tab, dato eller sjåfør endres
  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    // Oppdater tab i URL
    params.set("tab", activeTab);

    // Oppdater dato i URL
    if (selectedDate) {
      params.set("date", moment(selectedDate).format("YYYY-MM-DD"));
    } else {
      params.delete("date");
    }

    // Oppdater sjåfør i URL hvis vi er på route-tab
    if (activeTab === "route" && routeSelectedDriver) {
      params.set("driver", routeSelectedDriver);
    } else {
      params.delete("driver");
    }

    // Sett nye URL-parametere
    setSearchParams(params, { replace: true });
  }, [
    activeTab,
    selectedDate,
    routeSelectedDriver,
    searchParams,
    setSearchParams,
  ]);

  const handleStatusChange = (status: OrderStatus) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(status)) {
        // Allow removing any status, even if it would leave no statuses selected
        return prev.filter((s) => s !== status);
      }
      return [...prev, status];
    });
  };

  // --- Add Mutation Hook ---
  const updateScheduleMutation = useMutation(
    (scheduleData: {
      items: {
        routeId: string;
        routeStopNumber: number | null;
        status: string;
      }[];
    }) => {
      if (
        !userSession?.token?.accessToken ||
        !workspaceId ||
        !routeSelectedDriver
      ) {
        throw new Error(
          t(
            "planner:errors.missingScheduleData",
            "Missing required data to update schedule.",
          ),
        );
      }
      const formattedDate = moment(selectedDate).format("YYYY-MM-DD");
      return updateDriverSchedule(
        userSession.token.accessToken,
        workspaceId,
        routeSelectedDriver,
        formattedDate,
        scheduleData,
      );
    },
    {
      onSuccess: () => {
        // Refresh both the Firestore realtime reads and the API-paginated orders list
        invalidateOrders();
        // Also invalidate any driver route queries
        queryClient.invalidateQueries(["driverRoutes"]);
        queryClient.invalidateQueries(["driversApi"]);
        setShouldReset(true);
        setRoutePlannerState({
          hasChanges: false,
          changesCount: 0,
          routeStops: [],
          availableStops: [],
          hasValidationErrors: false,
        });
        setShowRouteSaveSuccessToast(true);
        setTimeout(() => setShowRouteSaveSuccessToast(false), 3000);
      },
      onError: (error) => {
        console.error("Error updating routes:", error);
      },
    },
  );
  // --- End Mutation Hook ---

  const handleSave = useCallback(async () => {
    if (!userSession?.token?.accessToken || !workspaceId) {
      console.error("Missing user session or workspace ID");
      return;
    }

    if (activeTab === "order") {
      // --- Order Planner Save Logic ---
      if (!plannerState.hasChanges || isUpdating) return;
      setIsUpdating(true);
      try {
        await Promise.all(
          plannerState.pendingChanges.map(async (change) => {
            try {
              await assignOrder(
                userSession.token.accessToken,
                workspaceId,
                change.orderId,
                change.driverId,
              );
            } catch (error) {
              console.error(`Failed to assign order ${change.orderId}:`, error);
            }
          }),
        );
        // Refresh both the Firestore realtime reads and the API-paginated orders list
        invalidateOrders();
        setShouldReset(true);
        setPlannerState({
          hasChanges: false,
          changesCount: 0,
          pendingChanges: [],
          localOrders: [],
        });
      } catch (error) {
        console.error("Error updating orders:", error);
      } finally {
        setIsUpdating(false);
      }
      // --- End Order Planner Save Logic ---
    } else {
      // --- Route Planner Save Logic (Using mutation) ---
      if (
        !routePlannerState.hasChanges ||
        updateScheduleMutation.isLoading ||
        !routeSelectedDriver
      )
        return;

      // Get scheduled stops (stops that are in the route)
      const scheduledStops = routePlannerState.routeStops
        .filter(
          (stop) =>
            stop.routeId &&
            typeof stop.routeId === "string" &&
            stop.routeId.length > 0,
        )
        .map((stop, index) => ({
          routeId: stop.routeId as string,
          routeStopNumber: index,
          status: "Scheduled",
        }));

      // Get unscheduled stops (available stops)
      const unscheduledStops = routePlannerState.availableStops
        .filter(
          (stop) =>
            stop.routeId &&
            typeof stop.routeId === "string" &&
            stop.routeId.length > 0,
        )
        .map((stop) => ({
          routeId: stop.routeId as string,
          routeStopNumber: null,
          status: "New",
        }));

      const scheduleData: {
        items: {
          routeId: string;
          routeStopNumber: number | null;
          status: string;
        }[];
      } = {
        items: [...scheduledStops, ...unscheduledStops],
      };

      console.log("Saving route data:", scheduleData); // Debug log
      console.log("Route stops:", routePlannerState.routeStops); // Debug - what we have in route
      console.log("Available stops:", routePlannerState.availableStops); // Debug - what we have in available
      updateScheduleMutation.mutate(scheduleData);
      // --- End Route Planner Save Logic ---
    }
  }, [
    activeTab,
    plannerState,
    routePlannerState,
    userSession,
    workspaceId,
    invalidateOrders,
    routeSelectedDriver,
    updateScheduleMutation,
    isUpdating,
  ]);

  const handleDiscard = useCallback(() => {
    setShouldReset(true);
    if (activeTab === "order") {
      setPlannerState({
        hasChanges: false,
        changesCount: 0,
        pendingChanges: [],
        localOrders: [],
      });
    } else {
      setRoutePlannerState({
        hasChanges: false,
        changesCount: 0,
        routeStops: [],
        availableStops: [],
        hasValidationErrors: false,
      });
    }
  }, [activeTab]);

  // Reset the shouldReset flag after it's been used
  useEffect(() => {
    if (shouldReset) {
      setShouldReset(false);
    }
  }, [shouldReset]);

  // Memoize the OrderPlanner component
  const orderPlanner = useMemo(
    () => (
      <OrderPlanner
        drivers={drivers}
        orders={orders}
        onChange={setPlannerState}
        reset={shouldReset}
        selectedDate={selectedDate}
        selectedStatuses={selectedStatuses}
        selectedDriver={orderSelectedDriver}
        onSelectDriver={setOrderSelectedDriver}
      />
    ),
    [
      drivers,
      orders,
      shouldReset,
      selectedDate,
      selectedStatuses,
      orderSelectedDriver,
    ],
  );

  // Add scroll event listener to make action buttons sticky
  useEffect(() => {
    const handleScroll = () => {
      const actionButtonsContainer = document.getElementById(
        "action-buttons-container",
      );
      const stickyContainer = document.getElementById("sticky-action-buttons");

      if (actionButtonsContainer && stickyContainer) {
        const rect = actionButtonsContainer.getBoundingClientRect();

        if (rect.top < 0) {
          if (stickyContainer.classList.contains("hidden")) {
            stickyContainer.classList.remove("hidden");
            stickyContainer.classList.add("block");
          }
        } else {
          if (!stickyContainer.classList.contains("hidden")) {
            stickyContainer.classList.add("hidden");
            stickyContainer.classList.remove("block");
          }
        }
      }
    };

    // Initial check and setup
    setTimeout(() => {
      handleScroll();
      // Force a check after components have fully rendered
      window.dispatchEvent(new Event("scroll"));
    }, 300);

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Håndter lukking av suksess-popupen
  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
    // Fjern showSuccessPopup og orderId fra URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("showSuccessPopup");
    newSearchParams.delete("orderId");
    setSearchParams(newSearchParams);
  };

  // Håndter planlegging av stopp
  const handlePlanStops = () => {
    setShowSuccessPopup(false);
    // Fjern showSuccessPopup og orderId fra URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("showSuccessPopup");
    newSearchParams.delete("orderId");
    setSearchParams(newSearchParams);
    // Sett aktivt tab til "route" (dette er alt som trengs, siden vi allerede er på planner-siden)
    setActiveTab("route");
  };

  const handleOpenDailyPlanner = () => {
    setShowSuccessPopup(false);
    const params = new URLSearchParams({
      date: moment(selectedDate).format("YYYY-MM-DD"),
    });
    if (orderIdFromUrl) params.set("orderId", orderIdFromUrl);
    navigate(`/daily-planner?${params.toString()}`);
  };

  // Håndter opprettelse av ny ordre
  const handleCreateNewOrder = () => {
    setShowSuccessPopup(false);
    // Naviger til opprett ordre-siden
    navigate("/orders/new");
  };

  return (
    <>
      {/* Sticky action buttons that appear when scrolling - outside PageLayout to be above navbar */}
      <div
        id="sticky-action-buttons"
        className={`hidden fixed top-0 left-0 right-0 z-[9999] bg-surface shadow-md border-b border-border w-full`}
        style={{ transition: "all 0.3s ease" }}
      >
        <div className="w-full py-7 px-5 flex justify-end items-center"></div>
      </div>

      <PageLayout isFooter={false}>
        <div className="flex flex-col h-full">
          {/* Tabs, Help, and Filters in a single row */}
          <div className={pageTabBar}>
            <Inline gap={4} className="px-4">
              {/* Driver Select removed from here */}

              <ul className="flex flex-wrap -mb-px text-sm font-medium text-center items-stretch">
                <TabButton
                  label={t("planner:order_tab")}
                  isSelected={activeTab === "order"}
                  onClick={() => setActiveTab("order")}
                />
                <TabButton
                  label={t("planner:route_tab")}
                  isSelected={activeTab === "route"}
                  onClick={() => setActiveTab("route")}
                />
              </ul>
              <HelpLabel
                content={
                  <StoryblokHelpBlock
                    storySlug="logibud-tms-web/help-popups/planner-page"
                    accessToken="Jf9NMAu4DXeo3fwdjd1w7wtt"
                  />
                }
                className="mt-1"
              />
              {/* Filters and actions inline with tabs */}
              <Inline gap={3} className="ml-auto">
                {activeTab === "order" ? (
                  <>
                    {/* Status filter dropdown */}
                    <OrderStatusFilterDropdown
                      selectedStatuses={selectedStatuses}
                      onStatusChange={handleStatusChange}
                      localOrders={plannerState.localOrders}
                    />
                    {/* Date picker */}
                    <div className="w-40">
                      <LGBDatepicker
                        select={(date: Date) => setSelectedDate(date)}
                        selectedDate={selectedDate || undefined}
                        showError={false}
                      />
                    </div>
                    {/* Save button for order planner */}
                    <Button
                      size="xs"
                      color="mainGreen"
                      disabled={!plannerState.hasChanges || isUpdating}
                      onClick={handleSave}
                      className="p-1.5"
                    >
                      {isUpdating ? (
                        <Spinner size="xs" />
                      ) : (
                        t("planner:order_planner.save_orders", "Save orders")
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    {/* Date picker */}
                    <div className="w-40">
                      <LGBDatepicker
                        select={(date: Date) => setSelectedDate(date)}
                        selectedDate={selectedDate || undefined}
                        showError={false}
                      />
                    </div>
                    {userSession?.user?.isAdmin && (
                      <Button
                        size="xs"
                        color="light"
                        onClick={handleOpenFirestoreRoutesForDate}
                        className="p-1.5"
                        aria-label="Åpne rutedata i Firebase"
                      >
                        Åpne rutedata i Firebase
                      </Button>
                    )}
                  </>
                )}
              </Inline>
            </Inline>
          </div>
          {/* Content area */}
          <div className="flex-1 min-h-0">
            {activeTab === "order" ? (
              <div className="px-4 pb-4 pt-4 h-full overflow-auto">
                {orderPlanner}
              </div>
            ) : (
              <div className="relative h-full">
                <RoutePlanner
                  selectedDate={selectedDate}
                  selectedDriver={routeSelectedDriver}
                  onSelectDriver={setRouteSelectedDriver}
                  drivers={drivers}
                  onChange={setRoutePlannerState}
                  reset={shouldReset}
                  onSaveChanges={handleSave}
                  onDiscardChanges={handleDiscard}
                  isSaving={updateScheduleMutation.isLoading}
                  hasRouteChanges={routePlannerState.hasChanges}
                  hasRouteValidationErrors={
                    routePlannerState.hasValidationErrors
                  }
                />
              </div>
            )}

            {showSuccessPopup && (
              <OrderSuccessPopup
                onClose={handleCloseSuccessPopup}
                onPlanStops={handlePlanStops}
                onOpenDailyPlanner={handleOpenDailyPlanner}
                onCreateNewOrder={handleCreateNewOrder}
                orderId={orderIdFromUrl || undefined}
              />
            )}

            {/* --- Add Success Toast Component (Styled like ValidationToast) --- */}
            {showRouteSaveSuccessToast && (
              <div className="fixed top-5 right-5 z-50">
                {" "}
                {/* Position top-right */}
                <Toast className="!bg-green-100 dark:!bg-green-700 !text-green-700 dark:!text-white min-w-[350px] max-w-[450px] shadow-lg">
                  {" "}
                  {/* Green background/text */}
                  <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-500 text-white dark:bg-green-600 dark:text-white">
                    {" "}
                    {/* Green icon background */}
                    <HiCheck className="h-6 w-6" /> {/* Success Icon */}
                  </div>
                  <div className="ml-3 text-base font-medium mr-8">
                    <p className="text-green-700 dark:text-white">
                      {" "}
                      {/* Green text */}
                      {/* Use a more specific key for the desired text */}
                      {t("planner:route_updated", "Route updated")}
                    </p>
                  </div>
                  <Toast.Toggle
                    onClick={() => setShowRouteSaveSuccessToast(false)}
                    // Adapt close button styles from ValidationToast (using green)
                    className="absolute top-2 right-2 bg-transparent text-green-700 hover:text-green-900 rounded-lg focus:ring-2 focus:ring-green-300 p-1.5 hover:bg-green-100/50 inline-flex h-8 w-8 dark:bg-transparent dark:text-white dark:hover:text-white dark:hover:bg-green-700/40"
                  />
                </Toast>
              </div>
            )}
            {/* --- End Success Toast Component --- */}
          </div>
        </div>
      </PageLayout>
    </>
  );
};
